import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import { User } from '../../types';
import KeyMetricsCards from './charts/KeyMetricsCards';
import PMCChart from './charts/PMCChart';
import PowerCurveChart from './charts/PowerCurveChart';
import WeeklyLoadChart from './charts/WeeklyLoadChart';
import CadenceChart from './charts/CadenceChart';
import WeaknessRadar from './charts/WeaknessRadar';
import RideTrendsChart from './charts/RideTrendsChart';
import TrainingStressChart from './charts/TrainingStressChart';
import PeriodizationTimeline from './charts/PeriodizationTimeline';
import WhoopTrendsChart from './charts/WhoopTrendsChart';
import ActivityZonesChart from './charts/ActivityZonesChart';
import './Analytics.css';

interface AnalyticsProps {
  onSwitchToData: () => void;
  user?: User;
}

interface AISummary {
  summary: string;
  charts: {
    key_metrics?: string;
    activity_zones?: string;
    pmc?: string;
    power_curve?: string;
    weekly_load?: string;
    cadence?: string;
    weakness_radar?: string;
    ride_trends?: string;
    training_stress?: string;
    whoop?: string;
  };
}

interface ProfileData {
  ftp_indoor: number | null;
  ftp_outdoor: number | null;
  weight_kg: number | null;
  w_per_kg: number | null;
  cp: number | null;
  w_prime: number | null;
  vo2max: number | null;
  training_load: number | null;
  cadence: {
    zones: any[];
    data_points: number;
    computed_at: string | null;
  };
  weakness: {
    available: boolean;
    message: string;
  };
}

const Analytics: React.FC<AnalyticsProps> = ({ onSwitchToData, user }) => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState<AISummary | null>(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);

  // Admin user switcher
  const [targetUserId, setTargetUserId] = useState<number | null>(null);
  const [targetUserName, setTargetUserName] = useState('');
  const [adminSearchQuery, setAdminSearchQuery] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState<{id: number; username: string; first_name: string; last_name: string}[]>([]);
  const adminSearchTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    if (adminSearchTimerRef.current) clearTimeout(adminSearchTimerRef.current);
    if (adminSearchQuery.length < 2) { setAdminSearchResults([]); return; }
    adminSearchTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/users/search-simple?q=${encodeURIComponent(adminSearchQuery)}`, { credentials: 'include' });
        if (res.ok) setAdminSearchResults(await res.json());
      } catch {}
    }, 300);
    return () => { if (adminSearchTimerRef.current) clearTimeout(adminSearchTimerRef.current); };
  }, [adminSearchQuery, user?.is_admin]);

  const handleAdminSelectUser = (u: {id: number; username: string; first_name: string; last_name: string}) => {
    setTargetUserId(u.id);
    setTargetUserName(`${u.first_name} ${u.last_name} (@${u.username})`);
    setAdminSearchQuery('');
    setAdminSearchResults([]);
    setProfile(null);
    setAiSummary(null);
    setLoading(true);
  };

  const handleAdminReset = () => {
    setTargetUserId(null);
    setTargetUserName('');
    setProfile(null);
    setAiSummary(null);
    setLoading(true);
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const url = targetUserId
          ? `${API_BASE_URL}/api/analytics/profile?target_user_id=${targetUserId}`
          : `${API_BASE_URL}/api/analytics/profile`;
        const res = await fetch(url, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to load profile');
        const data = await res.json();
        setProfile(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [targetUserId]);

  // Fetch AI summary on demand (when user clicks the button)
  const fetchAiSummary = async () => {
    setAiSummaryLoading(true);
    setAiSummary(null);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = new URLSearchParams();
      params.set('tz', tz);
      if (targetUserId) params.set('target_user_id', String(targetUserId));
      const url = `${API_BASE_URL}/api/analytics/ai-summary?${params.toString()}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.summary) setAiSummary(data);
      }
    } catch (e) {
      console.error('AI summary fetch failed:', e);
    } finally {
      setAiSummaryLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="analytics-loading">
          <div className="analytics-skeleton analytics-skeleton-cards" />
          <div className="analytics-skeleton analytics-skeleton-chart" />
          <div className="analytics-chart-grid">
            <div className="analytics-skeleton analytics-skeleton-chart-sm" />
            <div className="analytics-skeleton analytics-skeleton-chart-sm" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="analytics-empty-state">
          <p>Unable to load analytics data.</p>
          <p style={{ color: '#999', fontSize: 13 }}>{error}</p>
        </div>
      </div>
    );
  }

  const hasData = profile && (
    profile.ftp_indoor !== null ||
    profile.ftp_outdoor !== null ||
    profile.training_load !== null ||
    (profile.cadence && profile.cadence.zones && profile.cadence.zones.length > 0)
  );

  if (!hasData) {
    return (
      <div className="analytics-container">
        <WhoopTrendsChart targetUserId={targetUserId} />
        <div className="analytics-empty-state">
          <div className="analytics-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5">
              <path d="M3 3v18h18" />
              <path d="M7 16l4-8 4 4 4-6" />
            </svg>
          </div>
          <h3>No training data yet</h3>
          <p>Import rides with power data to see your analytics dashboard with fitness trends, power curve, and training load analysis.</p>
          <div className="analytics-empty-actions">
            <button className="analytics-cta-btn analytics-cta-primary" onClick={onSwitchToData}>
              Import FIT Files
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ChartComment: React.FC<{ chartKey: string }> = ({ chartKey }) => {
    const comment = aiSummary?.charts?.[chartKey as keyof AISummary['charts']];
    if (!comment) return null;
    return (
      <div className="analytics-ai-comment">
        <span className="analytics-ai-comment-icon">AI</span>
        <span>{comment}</span>
      </div>
    );
  };

  return (
    <div className="analytics-container">
      {user?.is_admin && (
        <div className="admin-user-switcher">
          <label>Admin:</label>
          {targetUserId ? (
            <div className="active-target">
              <span>{targetUserName}</span>
              <button onClick={handleAdminReset}>Reset</button>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search user..."
                value={adminSearchQuery}
                onChange={e => setAdminSearchQuery(e.target.value)}
              />
              {adminSearchResults.length > 0 && (
                <div className="search-results">
                  {adminSearchResults.map(u => (
                    <div key={u.id} onClick={() => handleAdminSelectUser(u)}>
                      {u.first_name} {u.last_name} (@{u.username}) #{u.id}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <PeriodizationTimeline targetUserId={targetUserId} />

      {/* AI Coach Summary — on demand */}
      {!aiSummary && !aiSummaryLoading && (
        <button className="analytics-ai-ask-btn" onClick={fetchAiSummary}>
          <span className="analytics-ai-ask-icon">AI</span>
          Ask AI Coach for opinion
        </button>
      )}
      {aiSummaryLoading && (
        <div className="analytics-ai-summary loading">
          <div className="analytics-ai-summary-header">
            <span className="analytics-ai-summary-badge">AI Coach</span>
            <span className="analytics-ai-summary-title">Generating analysis...</span>
          </div>
          <div className="analytics-skeleton" style={{ height: 120, marginTop: 12 }} />
        </div>
      )}
      {aiSummary?.summary && (
        <div className="analytics-ai-summary">
          <div className="analytics-ai-summary-header">
            <span className="analytics-ai-summary-badge">AI Coach</span>
            <span className="analytics-ai-summary-title">Training Analysis</span>
          </div>
          <p className="analytics-ai-summary-text">{aiSummary.summary}</p>
        </div>
      )}

      <KeyMetricsCards
        ftp_indoor={profile!.ftp_indoor}
        ftp_outdoor={profile!.ftp_outdoor}
        weight_kg={profile!.weight_kg}
        w_per_kg={profile!.w_per_kg}
        cp={profile!.cp}
        w_prime={profile!.w_prime}
        vo2max={profile!.vo2max}
      />
      <ChartComment chartKey="key_metrics" />

      <ActivityZonesChart targetUserId={targetUserId} />
      <ChartComment chartKey="activity_zones" />

      <PMCChart targetUserId={targetUserId} />
      <ChartComment chartKey="pmc" />

      <div className="analytics-chart-grid">
        <div>
          <PowerCurveChart targetUserId={targetUserId} />
          <ChartComment chartKey="power_curve" />
        </div>
        <div>
          <WeeklyLoadChart targetUserId={targetUserId} />
          <ChartComment chartKey="weekly_load" />
        </div>
      </div>

      <div className="analytics-chart-grid">
        <div>
          <CadenceChart targetUserId={targetUserId} />
          <ChartComment chartKey="cadence" />
        </div>
        <div>
          <WeaknessRadar targetUserId={targetUserId} />
          <ChartComment chartKey="weakness_radar" />
        </div>
      </div>

      <RideTrendsChart targetUserId={targetUserId} />
      <ChartComment chartKey="ride_trends" />

      <TrainingStressChart targetUserId={targetUserId} />
      <ChartComment chartKey="training_stress" />

      <WhoopTrendsChart targetUserId={targetUserId} />
      <ChartComment chartKey="whoop" />
    </div>
  );
};

export default Analytics;
